import {commonWebSocket} from '@services/common/common.constants';
import {createReducer} from 'redux-act';
import {clearChat, localSaveChatMessage} from './chat.actions';
import {webSocket} from './chat.constants';
import {ChatMsgStatus, IChat, IChatMessage, IChatUpdate, ILocallyStoredMsg, IMessageReadInfo} from '@app-types/websocket/chat.types';

const defaultState : {
  selectedChatId:number, displayChatInMobile:boolean, toxicTime:number,
  chats:IChat[], localMessages:ILocallyStoredMsg[],
  messageReadInfo:{[chatId:string]:{[msgId:string]:IMessageReadInfo}},
} = {
  selectedChatId: -1,
  displayChatInMobile: false,
  toxicTime: 0,
  chats:[],
  localMessages:[],
  messageReadInfo:{},
};

const reducer = createReducer<typeof defaultState>({}, defaultState);

const normalizeSendingTime = (sendingTime: any): number => {
  if (typeof sendingTime === "number" && Number.isFinite(sendingTime)) {
    return sendingTime;
  }

  // A timestamp delivered as a numeric string works in arithmetic but produces an
  // Invalid Date via the Date constructor, so coerce it here.
  const parsed = Number(sendingTime);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeMessages = (messages: IChatMessage[] | undefined): IChatMessage[] => {
  if (!Array.isArray(messages)) {
    return [];
  }

  return messages
    .filter((message) => message !== undefined && message !== null)
    .map((message) => ({
      ...message,
      msg: typeof message.msg === "string" ? message.msg : "",
      sendingTime: normalizeSendingTime(message.sendingTime),
    }));
};

const normalizeParticipants = (participants: any): { [key:string]: any } => {
  return participants && typeof participants === "object" ? {...participants} : {};
};

const normalizeChat = (chat: IChat): IChat => {
  return {
    ...chat,
    title: typeof chat.title === "string" ? chat.title : "",
    participants: normalizeParticipants(chat.participants),
    messages: normalizeMessages(chat.messages),
    messagesInTransit: normalizeMessages(chat.messagesInTransit),
    hidden: Array.isArray(chat.hidden) ? chat.hidden : [],
  };
};

const normalizeNumberList = (items: any): number[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.filter((item) => typeof item === "number" && Number.isFinite(item));
};

const normalizeLocalMessage = (message: ILocallyStoredMsg): ILocallyStoredMsg => {
  return {
    chatId: message.chatId,
    msg: typeof message.msg === "string" ? message.msg : "",
    uploadInternalIds: normalizeNumberList(message.uploadInternalIds),
    attachmentIds: normalizeNumberList(message.attachmentIds),
    uploadDraftSessionId: typeof message.uploadDraftSessionId === "number" ? message.uploadDraftSessionId : undefined,
  };
};

reducer
  .on(clearChat, (state: any, payload: any) => {
    return {
      ...defaultState,
    };
  })
  .on(commonWebSocket.special.MESSAGE_READ_INFO, (state, payload:{messageReadInfo:IMessageReadInfo}) => {
    const info = payload && payload.messageReadInfo;
    if (!info || !Number.isInteger(info.chatId) || info.chatId <= 0
      || !Number.isInteger(info.msgId) || info.msgId <= 0 || !Number.isFinite(info.asOf)
      || !info.participants || typeof info.participants !== 'object' || Array.isArray(info.participants)) {
      return state;
    }

    const cache = state.messageReadInfo || {};
    const chatInfo = cache[info.chatId] || {};
    const previous = chatInfo[info.msgId];
    if (previous && previous.asOf > info.asOf) {
      return state;
    }

    return {
      ...state,
      messageReadInfo: {
        ...cache,
        [info.chatId]: {...chatInfo, [info.msgId]: info},
      },
    };
  })
  .on(localSaveChatMessage, (state: any, payload: ILocallyStoredMsg) => {
    const localMessages:ILocallyStoredMsg[] = Array.isArray(state.localMessages) ? state.localMessages : [];
    if (!payload || typeof payload.chatId !== "number") {
      return {
        ...state,
        localMessages,
      };
    }

    const saved = localMessages.filter((msg: ILocallyStoredMsg) => msg && msg.chatId !== payload.chatId);
    const normalizedMessage = normalizeLocalMessage(payload);
    const uploadInternalIds = normalizedMessage.uploadInternalIds || [];
    const attachmentIds = normalizedMessage.attachmentIds || [];
    const isEmptyDraft = normalizedMessage.msg === ""
      && uploadInternalIds.length === 0
      && attachmentIds.length === 0;

    if (!isEmptyDraft) {
      saved.push({
        ...normalizedMessage,
        uploadInternalIds,
        attachmentIds,
      });
    }

    return {
      ...state,
      localMessages: saved,
    };
  })
  .on(commonWebSocket.snapshot.CHATS_SNAPSHOT, (state:any, payload:any) => {
    if (!Array.isArray(payload?.chats)) {
      return state;
    }

    const previousChats:IChat[] = Array.isArray(state.chats) ? state.chats : [];
    const chats:IChat[] = [];
    const snapshotChats:IChat[] = payload.chats;

    for (const chaSnap of snapshotChats) {
      if (!chaSnap) {
        continue;
      }
      const snapChat:IChat = normalizeChat(chaSnap);

      const previousChat = previousChats.find((chat: any) => {
        return chat.id === snapChat.id;
      });

      if (previousChat) {
        const messages = normalizeMessages(previousChat.messages);
        const messageIds = new Set(messages.map((message) => message.id));
        let reSortNeeded = false;
        for (const msg of snapChat.messages) {
          // Match the previous strict-equality lookup, including NaN (which
          // never matched itself). Existing cached copies still take priority.
          if (Number.isNaN(msg.id) || !messageIds.has(msg.id)) {
            messages.push(msg);
            messageIds.add(msg.id);
            reSortNeeded = true;
          }
        }
        if (reSortNeeded) {
          messages.sort((a, b) => a.id - b.id);
        }

        snapChat.messages = messages.slice();
      }

      chats.push(snapChat);
    }

    return {
      ...state,
      chats,
    };

  })
  .on(commonWebSocket.special.TOXIC_REPLY, (state, payload:any) => {
    return {
      ...state,
      toxic:payload.toxicReply,
      toxicTime:Date.now()
    };
  })
  .on(webSocket.NEW_CHAT, (state, payload: any) => {
    // console.log(`New chat ${JSON.stringify(payload.newChat)}`);
    if (!payload?.newChat || payload.newChat.id === undefined) {
      return state;
    }
    const stateChats:IChat[] = Array.isArray(state.chats) ? state.chats : [];
    const newChat:IChat = normalizeChat(payload.newChat);
    const chatToUpdateIndex = stateChats.findIndex((chat: any) => {
      return chat.id === newChat.id;
    });

    if (chatToUpdateIndex !== -1) {
      console.log(`Chat ${newChat.id} already exists in state`);
      return state;
    }
    return {
      ...state,
      chats: stateChats.concat(newChat)
    };
  })
  .on(webSocket.CHAT_UPDATE, (state, payload: any) => {
    if (!payload?.chatUpdate || payload.chatUpdate.id === undefined) {
      return state;
    }

    const stateChats:IChat[] = Array.isArray(state.chats) ? state.chats : [];
    const chatToUpdateIndex = stateChats.findIndex((chat: any) => {
      return chat.id === payload.chatUpdate.id;
    });

    if (chatToUpdateIndex === -1) {
      console.log(`Chat ${payload.chatUpdate.id} not found in state`);
      return state;
    } else {

      const update:IChatUpdate=payload.chatUpdate;
      // Redux persistence can restore data written by older versions. Every
      // update must normalize cached data, including receipt-only updates.
      const existingChat:IChat = normalizeChat(stateChats[chatToUpdateIndex]);
    
      const newMsgs:IChatMessage[] = normalizeMessages(update.messages);

      const participants: { [key: string]: any } = normalizeParticipants(existingChat.participants);
      Object.assign(participants, update.participants ? update.participants : {});

      if (update.participants) {
        for (const participantId of Object.keys(update.participants)) {
          const participant = update.participants[participantId];
          if (participant && participant.removed) {
            delete participants[participantId];
            console.log(`Participant ${participantId} removed from chat ${update.id}`);
          }
        }
      }
      
      
      const msgs:IChatMessage[] = [...existingChat.messages, ...newMsgs];
      msgs.sort((a, b) => a.id - b.id);

      const newMsgsInTransit:IChatMessage[] = normalizeMessages(update.inTransitMessages);
      const msgsInTransit:IChatMessage[] = normalizeMessages(existingChat.messagesInTransit);

      if (newMsgsInTransit.length > 0) {
        for (const t of newMsgsInTransit) {
          const transitIndex = msgsInTransit.findIndex((m) => m.id === t.id) 
          if (transitIndex === -1) {
            if (t.status === ChatMsgStatus.AWAIT_RESULT) {
              msgsInTransit.push(t);
            }
          } else if (t.status === ChatMsgStatus.AWAIT_RESULT) {
            msgsInTransit[transitIndex] = t;
          } else {
            msgsInTransit.splice(transitIndex, 1);
          }
        }
        msgsInTransit.sort((a, b) => a.id - b.id);
      }

      msgs.sort((a, b) => a.id - b.id);
      const updatedChat:IChat = {
        ...existingChat,
        ...payload.chatUpdate, // updates hidden title and icon
        title: typeof update.title === "string" ? update.title : existingChat.title,
        icon: typeof update.icon === "string" ? update.icon : existingChat.icon,
        hidden: Array.isArray(update.hidden) ? update.hidden : existingChat.hidden,
        messages: msgs,
        messagesInTransit: msgsInTransit,
        participants,
      };

      updatedChat.messages.sort((a, b) => a.id - b.id);
      const messageIds = new Set<number>();
      updatedChat.messages = updatedChat.messages.filter((msg) => {
        // Deduplicate the full history, not just incoming rows. Preserve the
        // first copy and the old strict-equality filter's rejection of NaN IDs.
        if (Number.isNaN(msg.id) || messageIds.has(msg.id)) {
          return false;
        }
        messageIds.add(msg.id);
        return true;
      });

      const chats = [
        ...stateChats.slice(0, chatToUpdateIndex),
        updatedChat,
        ...stateChats.slice(chatToUpdateIndex + 1),
      ];

      return {
        ...state,
        chats,
      };
    }
  });

export default reducer;



// WEBPACK FOOTER //
// ./src/services/chat/chat.reducer.ts